import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Badge,
  Button,
  Input,
  Select,
  Modal,
  ModalFooter,
  useConfirm,
  DropdownMenu,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
  DesignPartsSection,
} from '@tkpod/ui';
import type { DesignPartInfo } from '@tkpod/ui';
import { useApiClient, ApiClientError } from '@tkpod/api-client';
import {
  formatOrderedOptionValue,
  orderItemDisplayName,
  orderedSizeText,
  printSizeChangedNote,
  orderItemStoreListingTitle,
} from '@tkpod/shared';
import {
  getTransitionLists,
} from './orderStatusConstants';
import { OptionUploadVersionActions } from './OptionUploadVersionActions';

function formatOptionCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

function formatOptionBytes(size: number | null | undefined): string | null {
  if (size == null || !Number.isFinite(size) || size < 0) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function scanBadgeVariant(status: string): 'success' | 'danger' | 'warning' | 'default' {
  if (status === 'CLEAN') return 'success';
  if (status === 'INFECTED' || status === 'FAILED') return 'danger';
  if (status === 'SKIPPED') return 'default';
  return 'warning';
}

const CARRIERS = [
  { value: '', label: 'Select Carrier' },
  { value: 'UPS', label: 'UPS' },
  { value: 'USPS', label: 'USPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'DHL', label: 'DHL' },
  { value: 'Other', label: 'Other' },
];

function buildTrackingUrl(carrier: string, trackingNumber: string): string {
  const encoded = encodeURIComponent(trackingNumber);
  switch (carrier) {
    case 'UPS':
      return `https://www.ups.com/track?tracknum=${encoded}`;
    case 'USPS':
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encoded}`;
    case 'FedEx':
      return `https://www.fedex.com/fedextrack/?trknbr=${encoded}`;
    case 'DHL':
      return `https://www.dhl.com/us-en/home/tracking/tracking-parcel.html?submit=1&tracking-id=${encoded}`;
    default:
      return '';
  }
}

interface VariantSnapshot {
  options: Array<{ type: string; value: string; hex?: string }>;
  sku: string;
  supplierSku?: string | null;
  weightOz?: number | null;
  cost?: string;
}

interface OrderItem {
  id: string;
  title: string;
  /** Catalog product a PRODUCT line is built from. Null for custom products. */
  catalogTitle?: string | null;
  variantTitle: string | null;
  sku: string | null;
  quantity: number;
  cancelledQty?: number;
  unitPrice: string;
  totalPrice: string;
  uptId?: string | null;
  uptName: string | null;
  uptType: string | null;
  presetName: string | null;
  blankVariantId?: string | null;
  variantSnapshot?: VariantSnapshot | null;
  weightOz?: number | null;
  proofStatus: string;
  providerId?: string | null;
  providerName?: string | null;
  warehouseId?: string | null;
  routingSource?: string | null;
  fulfillmentProvider?: 'TK_POD' | 'SELF';
  externalSku?: string | null;
  designId?: string | null;
  designRefId?: string | null;
  partKey?: string | null;
  /** Stable public preview PNG URL (customer-facing, self-healing placeholder). */
  designPreviewUrlStable?: string | null;
  /** Stable public 300-DPI export PNG URL (fulfillment download). */
  designExportUrlStable?: string | null;
  designPreviewUrl?: string | null;
  /**
   * PRODUCT-context (published POD product) line preview: the ordered variant's
   * pre-rendered hero composite (mockup + design). Null for apparel / gangsheet.
   */
  variantPreviewUrl?: string | null;
  exportId?: string | null;
  exportStatus?: string | null;
  exportOutputUrl?: string | null;
  exportPreviewUrl?: string | null;
  sheetIndex?: number | null;
  actualWidthIn?: number | null;
  actualHeightIn?: number | null;
  designContext?: string | null;
  designerMode?: string | null;
  optionSubmissionId?: string | null;
  customization?: {
    submissionToken?: string | null;
    pricing?: {
      variantUnitPriceCents: number | null;
      surchargePerUnitCents: number | null;
      lines: { label: string; amountCentsPerUnit: number }[];
      formulaWidth?: number | null;
      formulaHeight?: number | null;
      formulaRateCents?: number | null;
      formulaUnitLabel?: string | null;
      displayFormulaUnitCents?: number | null;
      displayDiscountPercent?: number | null;
      displayDiscountCents?: number | null;
      displayCustomerUnitCents?: number | null;
      displayLineTotalCents?: number | null;
      costUnitCents?: number | null;
    } | null;
    selections: { label: string; value: string }[];
    uploads: {
      token?: string | null;
      label?: string | null;
      fileName: string | null;
      mimeType?: string | null;
      sizeBytes?: number | null;
      widthPx?: number | null;
      heightPx?: number | null;
      status?: string | null;
      scanStatus?: string | null;
      versionNo: number | null;
      thumbUrl: string | null;
      previewUrl: string | null;
      downloadUrl: string | null;
    }[];
  } | null;
  designParts?: DesignPartInfo[];
  /**
   * Per-line post-order edit permission (resolved server-side). Drives the
   * inline "Edit design" action. SELF / in-production lines resolve to
   * canEdit=false and render a disabled button with the reason.
   */
  editPermission?: {
    canEdit: boolean;
    reason?: string | null;
    message?: string | null;
  } | null;
}

interface ShipmentInfo {
  id: string;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  status: string;
  shippedAt: string | null;
  deliveredAt: string | null;
  fulfilledByProvider: { id: string; name: string } | null;
  items: { orderItemId: string; quantity: number }[];
  createdAt: string;
}

interface ProviderOption {
  id: string;
  name: string;
  status: string;
}

interface WarehouseOption {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
}

interface ProviderDetailResponse {
  id: string;
  warehouses: WarehouseOption[];
}

interface ProviderFulfillmentCardProps {
  orderId: string;
  status: string;
  providerId: string | null;
  providerName: string;
  items: OrderItem[];
  shipments: ShipmentInfo[];
  createdAt: string;
  isProviderUser?: boolean;
  canEditItems?: boolean;
  /**
   * Hides all fulfillment actions (override / shipment / cancel items).
   * Set for fully self-fulfilled orders — the merchant prints and ships
   * them, so TK-side fulfillment actions don't apply.
   */
  actionsDisabled?: boolean;
  onFeedback?: (type: 'success' | 'error', text: string) => void;
  /**
   * Launch the designer for a line's bound design (post-order revision flow).
   * When omitted, the inline "Edit design" action is hidden.
   */
  onEditDesign?: (itemId: string) => void;
  /** Item id currently minting a launch token — drives the per-row spinner label. */
  editPendingItemId?: string | null;
}

type ActionType = 'cancel' | 'shipment' | 'override';
type ActionStep = 'select-items' | 'fill-details';

export function ProviderFulfillmentCard({
  orderId,
  status,
  providerId,
  providerName,
  items,
  shipments,
  createdAt,
  isProviderUser = false,
  canEditItems = true,
  actionsDisabled = false,
  onFeedback,
  onEditDesign,
  editPendingItemId,
}: ProviderFulfillmentCardProps) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const { isTerminal } = getTransitionLists(status);
  const isUnassigned = !providerId;

  const itemIds = new Set(items.map((i) => i.id));
  const relevantShipments = shipments.filter((s) =>
    s.items.some((si) => itemIds.has(si.orderItemId)),
  );

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  // Action wizard state
  const [activeAction, setActiveAction] = useState<ActionType | null>(null);
  const [actionStep, setActionStep] = useState<ActionStep>('select-items');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [qtys, setQtys] = useState<Record<string, number>>({});

  // Shipment form
  const [carrier, setCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [customTrackingUrl, setCustomTrackingUrl] = useState('');

  // Cancel confirm
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Add tracking inline form
  const [showAddTracking, setShowAddTracking] = useState(false);
  const [trackCarrier, setTrackCarrier] = useState('');
  const [trackNumber, setTrackNumber] = useState('');
  const [trackCustomUrl, setTrackCustomUrl] = useState('');

  // Override provider state
  const [perItemProvider, setPerItemProvider] = useState<Record<string, string>>({});
  const [perItemWarehouse, setPerItemWarehouse] = useState<Record<string, string>>({});
  const [overrideReason, setOverrideReason] = useState('');

  const getItemFulfilledQty = useCallback(
    (itemId: string) =>
      shipments
        .filter((s) => s.status !== 'CANCELLED')
        .flatMap((s) => s.items)
        .filter((si) => si.orderItemId === itemId)
        .reduce((sum, si) => sum + si.quantity, 0),
    [shipments],
  );

  const getMaxForAction = useCallback(
    (item: OrderItem, action: ActionType) => {
      if (action === 'override') return item.quantity;
      const cancelled = item.cancelledQty ?? 0;
      if (action === 'cancel') return item.quantity - cancelled;
      const effectiveQty = item.quantity - cancelled;
      return effectiveQty - getItemFulfilledQty(item.id);
    },
    [getItemFulfilledQty],
  );

  const startAction = useCallback((type: ActionType) => {
    setActiveAction(type);
    setActionStep('select-items');
    setSelectedIds(new Set());
    setQtys({});
    setCarrier('');
    setTrackingNumber('');
    setCustomTrackingUrl('');
    if (type === 'override') {
      const provMap: Record<string, string> = {};
      const whMap: Record<string, string> = {};
      for (const item of items) {
        if (item.providerId) provMap[item.id] = item.providerId;
        if (item.warehouseId) whMap[item.id] = item.warehouseId;
      }
      setPerItemProvider(provMap);
      setPerItemWarehouse(whMap);
      setOverrideReason('');
    }
  }, [items]);

  const resetAction = useCallback(() => {
    setActiveAction(null);
    setActionStep('select-items');
    setSelectedIds(new Set());
    setQtys({});
    setCarrier('');
    setTrackingNumber('');
    setCustomTrackingUrl('');
    setShowCancelConfirm(false);
    setPerItemProvider({});
    setPerItemWarehouse({});
    setOverrideReason('');
  }, []);

  const toggleItem = useCallback(
    (itemId: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(itemId)) {
          next.delete(itemId);
          setQtys((q) => {
            const nq = { ...q };
            delete nq[itemId];
            return nq;
          });
        } else {
          next.add(itemId);
          const item = items.find((i) => i.id === itemId);
          if (item && activeAction) {
            const max = getMaxForAction(item, activeAction);
            setQtys((q) => ({ ...q, [itemId]: max }));
          }
        }
        return next;
      });
    },
    [items, activeAction, getMaxForAction],
  );

  const proceedToDetails = useCallback(() => {
    if (activeAction === 'cancel') {
      setShowCancelConfirm(true);
    } else {
      setActionStep('fill-details');
    }
  }, [activeAction]);

  const hasSelection = activeAction === 'override'
    ? selectedIds.size > 0
    : selectedIds.size > 0 && Object.values(qtys).some((q) => q > 0);

  // Mutations
  const switchToSelfMutation = useMutation({
    mutationFn: (itemId: string) =>
      api.post(`/api/v1/admin/orders/${orderId}/items/${itemId}/switch-to-self`, {}),
    onSuccess: () => {
      onFeedback?.('success', 'Line switched to self-fulfill');
      void queryClient.refetchQueries({ queryKey: ['admin', 'orders', orderId] });
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiClientError ? err.message : err.message;
      onFeedback?.('error', msg || 'Could not switch this line to self-fulfill');
    },
  });

  const requestSwitchToSelf = async (itemId: string) => {
    const ok = await confirm({
      title: 'Switch this line to self-fulfill',
      description:
        'Transfer Kingdom will stop producing this line. A queued provider submission is removed and the wholesale cost is recomputed. This is refused once the provider has the job or settlement is captured.',
      confirmLabel: 'Switch to self-fulfill',
      tone: 'danger',
    });
    if (ok) switchToSelfMutation.mutate(itemId);
  };

  const cancelMutation = useMutation({
    mutationFn: () => {
      const payload = Object.entries(qtys)
        .filter(([id, q]) => selectedIds.has(id) && q > 0)
        .map(([orderItemId, cancelQty]) => ({ orderItemId, cancelQty }));
      return api.post(`/api/v1/admin/orders/${orderId}/cancel-items`, { items: payload });
    },
    onSuccess: () => {
      onFeedback?.('success', 'Items cancelled successfully');
      resetAction();
      void queryClient.refetchQueries({ queryKey: ['admin', 'orders', orderId] });
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiClientError && err.apiError?.details?.length
        ? err.apiError.details.map((d) => d.message).join('. ')
        : err.message || 'Failed to cancel items';
      onFeedback?.('error', msg);
    },
  });

  const shipmentMutation = useMutation({
    mutationFn: () => {
      const trackingUrl = customTrackingUrl || buildTrackingUrl(carrier, trackingNumber);
      const shipmentItems = Object.entries(qtys)
        .filter(([id, q]) => selectedIds.has(id) && q > 0)
        .map(([orderItemId, quantity]) => ({ orderItemId, quantity }));
      return api.post(`/api/v1/admin/orders/${orderId}/shipments`, {
        items: shipmentItems,
        carrier,
        trackingNumber,
        trackingUrl: trackingUrl || undefined,
      });
    },
    onSuccess: () => {
      onFeedback?.('success', 'Shipment created successfully');
      resetAction();
      void queryClient.refetchQueries({ queryKey: ['admin', 'orders', orderId] });
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiClientError && err.apiError?.details?.length
        ? err.apiError.details.map((d) => d.message).join('. ')
        : err.message || 'Failed to create shipment';
      onFeedback?.('error', msg);
    },
  });

  const addTrackingMutation = useMutation({
    mutationFn: () => {
      const trackingUrl = trackCustomUrl || buildTrackingUrl(trackCarrier, trackNumber);
      const shipmentItems = items
        .map((item) => {
          const cancelled = item.cancelledQty ?? 0;
          const effectiveQty = item.quantity - cancelled;
          const fulfilledQty = getItemFulfilledQty(item.id);
          const remaining = effectiveQty - fulfilledQty;
          return remaining > 0 ? { orderItemId: item.id, quantity: remaining } : null;
        })
        .filter((x): x is { orderItemId: string; quantity: number } => x !== null);

      if (shipmentItems.length === 0) {
        return Promise.reject(new Error('No items to ship'));
      }

      return api.post(`/api/v1/admin/orders/${orderId}/shipments`, {
        items: shipmentItems,
        carrier: trackCarrier,
        trackingNumber: trackNumber,
        trackingUrl: trackingUrl || undefined,
      });
    },
    onSuccess: () => {
      onFeedback?.('success', 'Tracking added and shipment created');
      setShowAddTracking(false);
      setTrackCarrier('');
      setTrackNumber('');
      setTrackCustomUrl('');
      void queryClient.refetchQueries({ queryKey: ['admin', 'orders', orderId] });
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiClientError && err.apiError?.details?.length
        ? err.apiError.details.map((d) => d.message).join('. ')
        : err.message || 'Failed to add tracking';
      onFeedback?.('error', msg);
    },
  });

  const overrideMutation = useMutation({
    mutationFn: () => {
      const validItems = [...selectedIds]
        .filter((id) => perItemProvider[id] && perItemWarehouse[id])
        .map((id) => ({
          orderItemId: id,
          providerId: perItemProvider[id],
          warehouseId: perItemWarehouse[id],
        }));
      if (validItems.length === 0) {
        return Promise.reject(new Error('Please select provider and warehouse for all items'));
      }
      const payload = {
        items: validItems,
        reason: overrideReason.trim() || undefined,
      };
      return api.post(`/api/v1/admin/orders/${orderId}/items/override-providers`, payload);
    },
    onSuccess: () => {
      onFeedback?.('success', 'Providers assigned successfully');
      resetAction();
      void queryClient.refetchQueries({ queryKey: ['admin', 'orders', orderId] });
    },
    onError: (err: Error) => {
      const msg = err instanceof ApiClientError && err.apiError?.details?.length
        ? err.apiError.details.map((d) => d.message).join('. ')
        : err.message || 'Failed to assign providers';
      onFeedback?.('error', msg);
    },
  });

  const canSubmitShipment =
    carrier && trackingNumber &&
    Object.entries(qtys).some(([id, q]) => selectedIds.has(id) && q > 0) &&
    !shipmentMutation.isPending;

  const canSubmitTracking = trackCarrier && trackNumber && !addTrackingMutation.isPending;

  const canSubmitOverride =
    selectedIds.size > 0 &&
    [...selectedIds].every((id) => perItemProvider[id] && perItemWarehouse[id]) &&
    !overrideMutation.isPending;

  const hasUnfulfilledItems = items.some((item) => {
    const cancelled = item.cancelledQty ?? 0;
    const effectiveQty = item.quantity - cancelled;
    const fulfilledQty = getItemFulfilledQty(item.id);
    return effectiveQty - fulfilledQty > 0;
  });

  return (
    <>
      <Card padding="none">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-5 pt-4 pb-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold ${
              isUnassigned
                ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300'
                : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
            }`}>
              {isUnassigned ? '?' : <ProviderIcon />}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-text-primary truncate">
                  {providerName}
                </h3>
                <Badge variant={isUnassigned ? 'warning' : 'default'} size="sm">
                  {itemCount} item{itemCount !== 1 ? 's' : ''}
                </Badge>
              </div>
              <p className="text-[10px] text-text-tertiary mt-0.5">
                {new Date(createdAt).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {(() => {
              const showOverride = !isProviderUser && status !== 'SHIPPED';
              const showShipment = hasUnfulfilledItems;
              const showCancelItems = !isProviderUser && canEditItems && status !== 'CANCELLED' && status !== 'DELIVERED';
              const hasMenuItems = showOverride || showShipment || showCancelItems;
              if (actionsDisabled || isTerminal || activeAction || !hasMenuItems) return null;
              return (
                <DropdownMenu align="right">
                  <DropdownTrigger>
                    <span className="inline-flex items-center justify-center rounded-full w-8 h-8 text-text-tertiary hover:bg-surface-secondary hover:text-text-primary transition-colors cursor-pointer">
                      <EllipsisIcon />
                    </span>
                  </DropdownTrigger>
                  <DropdownContent minWidth="min-w-[220px]">
                    {showOverride && (
                      <>
                        <DropdownItem onClick={() => startAction('override')}>
                          Override Provider
                        </DropdownItem>
                        <DropdownSeparator />
                      </>
                    )}
                    {showShipment && (
                      <DropdownItem onClick={() => startAction('shipment')}>
                        Create Shipment
                      </DropdownItem>
                    )}
                    {showCancelItems && (
                      <DropdownItem danger onClick={() => startAction('cancel')}>
                        Cancel Items
                      </DropdownItem>
                    )}
                  </DropdownContent>
                </DropdownMenu>
              );
            })()}
          </div>
        </div>

        {/* Action mode banner */}
        {activeAction && actionStep === 'select-items' && (
          <div
            className={`border-t-2 px-5 py-2.5 flex items-center justify-between ${
              activeAction === 'cancel'
                ? 'border-red-300 dark:border-red-500/40 bg-red-50/60 dark:bg-red-500/5'
                : activeAction === 'override'
                  ? 'border-violet-300 dark:border-violet-500/40 bg-violet-50/60 dark:bg-violet-500/5'
                  : 'border-blue-300 dark:border-blue-500/40 bg-blue-50/60 dark:bg-blue-500/5'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${
                activeAction === 'cancel'
                  ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300'
                  : activeAction === 'override'
                    ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400'
                    : 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
              }`}>
                {activeAction === 'cancel' ? <CancelIcon size={10} /> : activeAction === 'override' ? <OverrideIcon /> : <PackageIcon />}
              </span>
              <span className={`text-xs font-semibold ${
                activeAction === 'cancel'
                  ? 'text-red-700 dark:text-red-400'
                  : activeAction === 'override'
                    ? 'text-violet-700 dark:text-violet-400'
                    : 'text-blue-700 dark:text-blue-400'
              }`}>
                {activeAction === 'cancel' ? 'Cancel Items' : activeAction === 'override' ? 'Override Provider' : 'Create Shipment'}
              </span>
              <span className="text-[11px] text-text-tertiary">
                — Select items below{selectedIds.size > 0 ? ` (${selectedIds.size} selected)` : ''}
              </span>
            </div>
            <button
              onClick={resetAction}
              className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-text-primary transition-colors rounded-md px-2 py-1 hover:bg-surface-secondary"
            >
              <CloseIcon />
              Cancel
            </button>
          </div>
        )}

        {/* Items list */}
        <div className={`border-t border-border ${activeAction && actionStep !== 'select-items' ? 'opacity-50 pointer-events-none' : ''}`}>
          {items.map((item, idx) => {
            const displaySku = item.sku ?? item.variantSnapshot?.sku ?? null;
            const cancelled = item.cancelledQty ?? 0;
            const isFullyCancelled = cancelled >= item.quantity;
            const fulfilledQty = getItemFulfilledQty(item.id);
            const effectiveQty = item.quantity - cancelled;
            const isFullyFulfilled = fulfilledQty >= effectiveQty && effectiveQty > 0;

            const showCheckbox = !!activeAction && actionStep === 'select-items';
            const isEligible = showCheckbox && (activeAction === 'override' || getMaxForAction(item, activeAction!) > 0);
            const isSelected = selectedIds.has(item.id);
            const maxQty = activeAction ? getMaxForAction(item, activeAction) : 0;

            const selectedHighlight = isSelected && activeAction === 'cancel'
              ? 'bg-red-50/50 dark:bg-red-500/5'
              : isSelected && activeAction === 'override'
                ? 'bg-violet-50/50 dark:bg-violet-500/5'
                : isSelected && activeAction === 'shipment'
                  ? 'bg-blue-50/50 dark:bg-blue-500/5'
                  : '';

            return (
              <div
                key={item.id}
                className={`px-5 py-4 ${idx < items.length - 1 ? 'border-b border-border' : ''} ${
                  isFullyCancelled ? 'opacity-40' : ''
                } ${selectedHighlight} transition-colors`}
              >
                <div className="flex items-start gap-3">
                  {showCheckbox && (
                    <div className="pt-1 shrink-0">
                      {isEligible ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleItem(item.id)}
                          className="h-4 w-4 rounded border-border accent-brand-primary cursor-pointer"
                        />
                      ) : (
                        <input type="checkbox" disabled className="h-4 w-4 rounded border-border opacity-30" />
                      )}
                    </div>
                  )}

                  {(() => {
                    // Preference order:
                    //   1. Stable customer-facing preview URL (always available —
                    //      self-heals from placeholder to real PNG as soon as the
                    //      render job completes).
                    //   2. Export-job preview URL (internal/legacy orders).
                    //   3. Design preview URL (legacy single-sheet fallback).
                    // Gangsheet lines (sheetIndex set) own a single SHEET
                    // part — use its preview so each line shows its own sheet
                    // instead of an empty placeholder.
                    const sheetPreview =
                      item.sheetIndex != null
                        ? item.designParts?.find((p) => p.kind === 'SHEET')
                            ?.previewUrl ?? null
                        : null;
                    // PRODUCT-context lines: prefer the ordered variant's hero
                    // composite (mockup + design) over the generic stable preview.
                    const uploadPreview = item.customization?.uploads.find(
                      (file) => file.thumbUrl || file.previewUrl,
                    );
                    const previewSrc = item.variantPreviewUrl
                      || item.designPreviewUrlStable
                      || item.exportPreviewUrl
                      || (item.sheetIndex == null ? item.designPreviewUrl : sheetPreview)
                      || uploadPreview?.thumbUrl
                      || uploadPreview?.previewUrl;
                    const optionsOnly =
                      !!item.optionSubmissionId || item.designerMode === 'NONE';
                    return previewSrc ? (
                      <img
                        src={previewSrc}
                        alt="Design preview"
                        className="h-12 w-12 shrink-0 rounded-lg border border-border object-cover bg-surface-secondary"
                      />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-secondary text-lg">
                        {item.exportStatus === 'COMPLETED' || optionsOnly ? '🖨️' : (
                          <svg className="h-5 w-5 animate-spin text-text-tertiary" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        )}
                      </div>
                    );
                  })()}

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold text-text-primary leading-snug ${isFullyCancelled ? 'line-through' : ''}`}>
                      {orderItemDisplayName(item)}
                    </p>
                    {orderItemStoreListingTitle(item) && (
                      <p className="mt-0.5 text-xs text-text-tertiary">
                        Store listing: {orderItemStoreListingTitle(item)}
                      </p>
                    )}

                    {item.presetName ? (
                      <p className="mt-0.5 text-xs text-text-secondary">{item.presetName}</p>
                    ) : (item.variantSnapshot?.options?.length ?? 0) > 0 ? (
                      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-1 text-xs text-text-secondary">
                        {item.variantSnapshot!.options.map((opt, i) => {
                          const sized = orderedSizeText(item.customization);
                          const sizeNote = i === 0 ? printSizeChangedNote(item.customization) : null;
                          const label =
                            sized && (item.optionSubmissionId || item.designerMode === 'NONE')
                              ? `${opt.value}: ${sized}${sizeNote ? ` ${sizeNote}` : ''}`
                              : item.optionSubmissionId || item.designerMode === 'NONE'
                                ? `${opt.type}: ${opt.value}`
                                : opt.value;
                          return (
                          <span key={opt.type} className="inline-flex items-center gap-1">
                            {i > 0 && <span className="text-text-tertiary">/</span>}
                            {opt.hex && (
                              <span
                                className="inline-block w-3 h-3 rounded-full border border-border shadow-sm"
                                style={{ backgroundColor: opt.hex }}
                              />
                            )}
                            {label}
                          </span>
                          );
                        })}
                      </div>
                    ) : null}

                    {item.designerMode === 'NONE' && !item.optionSubmissionId ? (
                      <p className="mt-1 text-xs text-warning">
                        No customer file on this line — provider submit blocked
                      </p>
                    ) : null}
                    {item.customization?.submissionToken && (
                      <p className="mt-1 font-mono text-[10px] text-text-tertiary">
                        {item.customization.submissionToken}
                      </p>
                    )}
                    {item.customization?.pricing && (
                      <div className="mt-1 flex flex-col gap-0.5">
                        {item.customization.pricing.variantUnitPriceCents != null && (
                          <p className="text-xs text-text-secondary">
                            Variant:{' '}
                            <span className="font-medium text-text-primary">
                              {formatOptionCents(item.customization.pricing.variantUnitPriceCents)}
                            </span>
                          </p>
                        )}
                        {item.customization.pricing.formulaWidth != null &&
                          item.customization.pricing.formulaHeight != null && (
                          <p className="text-xs text-text-secondary">
                            Area {item.customization.pricing.formulaWidth} × {item.customization.pricing.formulaHeight}{' '}
                            {item.customization.pricing.formulaUnitLabel ?? ''}
                            {item.customization.pricing.formulaRateCents != null
                              ? ` × ${formatOptionCents(item.customization.pricing.formulaRateCents)}`
                              : ''}
                            {item.customization.pricing.displayFormulaUnitCents != null
                              ? ` = ${formatOptionCents(item.customization.pricing.displayFormulaUnitCents)}`
                              : ''}
                          </p>
                        )}
                        {item.customization.pricing.displayDiscountPercent != null &&
                          item.customization.pricing.displayDiscountPercent > 0 && (
                          <p className="text-xs text-text-secondary">
                            Tier {item.customization.pricing.displayDiscountPercent}% off
                            {item.customization.pricing.displayCustomerUnitCents != null
                              ? `, ${formatOptionCents(item.customization.pricing.displayCustomerUnitCents)} each`
                              : ''}
                          </p>
                        )}
                        {item.customization.pricing.costUnitCents != null && (
                          <p className="text-xs text-text-secondary">
                            Cost:{' '}
                            <span className="font-medium text-text-primary">
                              {formatOptionCents(item.customization.pricing.costUnitCents)}
                            </span>
                          </p>
                        )}
                        {item.customization.pricing.lines.map((line) => (
                          <p key={line.label} className="text-xs text-text-secondary">
                            {line.label}:{' '}
                            <span className="font-medium text-text-primary">
                              {formatOptionCents(line.amountCentsPerUnit)}
                            </span>
                          </p>
                        ))}
                      </div>
                    )}
                    {item.customization && item.customization.selections.length > 0 && (
                      <div className="mt-1 flex flex-col gap-0.5">
                        {item.customization.selections.map((row) => (
                          <p key={`${row.label}:${row.value}`} className="text-xs text-text-secondary">
                            {row.label}: <span className="font-medium text-text-primary">{formatOrderedOptionValue(row.value)}</span>
                          </p>
                        ))}
                      </div>
                    )}
                    {item.customization && item.customization.uploads.length > 0 && (
                      <div className="mt-1.5 flex flex-col gap-1.5">
                        {item.customization.uploads.map((file, index) => (
                          <div key={file.token ?? `upload-${index}`} className="flex items-start gap-2">
                            {file.thumbUrl ? (
                              <img
                                src={file.thumbUrl}
                                alt=""
                                className="h-16 w-16 rounded border border-border object-contain bg-[repeating-conic-gradient(#f3f4f6_0%_25%,#fff_0%_50%)] [background-size:12px_12px]"
                              />
                            ) : (
                              <div className="flex h-8 w-8 items-center justify-center rounded border border-dashed border-border bg-surface-secondary text-[9px] text-text-tertiary">
                                File
                              </div>
                            )}
                            <div className="min-w-0">
                              {file.label && (
                                <p className="text-xs font-medium text-text-primary">{file.label}</p>
                              )}
                              <div className="flex flex-wrap items-center gap-1.5">
                                {file.versionNo != null && (
                                  <Badge variant="info" size="sm">v{file.versionNo}</Badge>
                                )}
                                {file.scanStatus && (
                                  <Badge variant={scanBadgeVariant(file.scanStatus)} size="sm">
                                    {file.scanStatus}
                                  </Badge>
                                )}
                                {file.previewUrl && (
                                  <a
                                    href={file.previewUrl}
                                    className="text-xs font-medium text-accent hover:underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Preview
                                  </a>
                                )}
                                {file.downloadUrl && (
                                  <a
                                    href={file.downloadUrl}
                                    className="text-xs font-medium text-accent hover:underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Download
                                  </a>
                                )}
                              </div>
                              <p className="text-[10px] text-text-tertiary">
                                {[
                                  file.mimeType,
                                  formatOptionBytes(file.sizeBytes),
                                  file.widthPx != null && file.heightPx != null
                                    ? `${file.widthPx}×${file.heightPx}`
                                    : null,
                                ].filter(Boolean).join(' · ') || 'No preview'}
                              </p>
                              {file.token && (
                                <OptionUploadVersionActions
                                  token={file.token}
                                  fileLabel={file.label}
                                  basePath={`/api/v1/admin/option-uploads/${file.token}`}
                                  orderQueryKey={['admin', 'orders']}
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Qty</span>
                        <span className="text-xs font-medium text-text-primary tabular-nums">{item.quantity}</span>
                      </div>
                      {displaySku && (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">SKU</span>
                          <span className="font-mono text-xs text-text-primary bg-surface-secondary px-2 py-0.5 rounded border border-border">
                            {displaySku}
                          </span>
                        </div>
                      )}
                      {item.weightOz != null && item.weightOz > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Weight</span>
                          <span className="text-xs text-text-primary">{item.weightOz} oz</span>
                        </div>
                      )}
                      {item.externalSku && (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Ext. SKU</span>
                          <span className="font-mono text-xs text-violet-700 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/10 px-2 py-0.5 rounded border border-violet-200 dark:border-violet-500/20">
                            {item.externalSku}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Removed the legacy "Export → Download PNG"
                        (`item.exportOutputUrl`) block: it is the per-order
                        Export pipeline output, which is stale/shared across a
                        multi-sheet gangsheet and only ever appears after a
                        post-order edit re-runs the legacy lane. The live
                        per-sheet print files rendered by <DesignPartsSection>
                        below are the single source of truth (portal parity). */}
                    {/* Removed the stale "Design → Download Design PNG"
                        (`designExportUrlStable`) link: it is an unversioned,
                        order-time snapshot URL that did NOT refresh after a
                        post-order design edit, so it duplicated — and went
                        stale against — the live per-part download rendered by
                        <DesignPartsSection> below. The live DesignPart export
                        is now the single source of truth (portal parity). */}
                    {/* "View Preview" points at the design-level preview —
                        correct for apparel (composite) but identical across every
                        gangsheet line of an order, so it's misleading there. Each
                        gangsheet line already shows its own sheet in the thumbnail
                        and Print files. Hide for gangsheet lines (a SHEET design
                        part is present; apparel never has one). */}
                    {!(item.designParts ?? []).some((p) => p.kind === 'SHEET') &&
                      (item.exportPreviewUrl || (item.sheetIndex == null && item.designPreviewUrl)) && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Preview</span>
                        <a
                          href={item.exportPreviewUrl || item.designPreviewUrl!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-text-secondary hover:text-accent hover:underline"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Preview
                        </a>
                      </div>
                    )}
                    {item.actualWidthIn != null && item.actualHeightIn != null && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary w-14 shrink-0">Print</span>
                        <span className="text-xs text-text-primary">{item.actualWidthIn}&times;{item.actualHeightIn}&quot;</span>
                      </div>
                    )}

                    <DesignPartsSection
                      parts={item.designParts}
                      previewImageUrl={item.variantPreviewUrl}
                    />

                    {/* Inline per-line "Edit design" — design-bound lines only.
                        Permission is resolved server-side; SELF / in-production
                        lines render disabled with the reason. */}
                    {!isTerminal &&
                      !isProviderUser &&
                      canEditItems &&
                      !actionsDisabled &&
                      !isFullyCancelled &&
                      (item.fulfillmentProvider ?? 'TK_POD') === 'TK_POD' && (
                      <div className="mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          loading={switchToSelfMutation.isPending && switchToSelfMutation.variables === item.id}
                          disabled={switchToSelfMutation.isPending}
                          onClick={() => {
                            void requestSwitchToSelf(item.id);
                          }}
                        >
                          Switch to self-fulfill
                        </Button>
                      </div>
                    )}
                    {onEditDesign && item.editPermission && item.designId && (() => {
                      const perm = item.editPermission;
                      const canEdit = (perm?.canEdit ?? false) && !!item.uptId;
                      const isOpening = editPendingItemId === item.id;
                      return (
                        <div className="mt-2">
                          <Button
                            variant={canEdit ? 'secondary' : 'outline'}
                            size="sm"
                            disabled={!canEdit || isOpening}
                            loading={isOpening}
                            title={!canEdit ? perm?.message ?? undefined : undefined}
                            onClick={() => {
                              if (canEdit) onEditDesign(item.id);
                            }}
                          >
                            Edit design
                          </Button>
                          {!canEdit && perm?.message && (
                            <p className="mt-0.5 text-[10px] text-text-tertiary">
                              {perm.message}
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      {cancelled > 0 && (
                        <Badge variant="danger" size="sm">
                          {cancelled} cancelled
                        </Badge>
                      )}
                      {fulfilledQty > 0 && (
                        <Badge variant={isFullyFulfilled ? 'success' : 'info'} size="sm">
                          {fulfilledQty}/{effectiveQty} shipped
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="text-right shrink-0 space-y-0.5">
                    {isSelected && showCheckbox && activeAction !== 'override' ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min={1}
                          max={maxQty}
                          value={qtys[item.id] ?? maxQty}
                          onChange={(e) => {
                            const val = Math.min(Math.max(1, Number(e.target.value)), maxQty);
                            setQtys((prev) => ({ ...prev, [item.id]: val }));
                          }}
                          className="w-14 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-primary text-center font-medium"
                        />
                        <span className="text-[10px] text-text-tertiary whitespace-nowrap">/ {maxQty}</span>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm font-semibold text-text-primary tabular-nums">
                          ${item.totalPrice}
                        </p>
                        <p className="text-xs text-text-tertiary tabular-nums">
                          ${item.unitPrice}
                          <span className="mx-1">×</span>
                          {item.quantity}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Action footer — Continue button (step 1) */}
        {activeAction && actionStep === 'select-items' && (
          <div
            className={`border-t-2 px-5 py-3 flex items-center gap-2 ${
              activeAction === 'cancel'
                ? 'border-red-300 dark:border-red-500/40 bg-red-50/60 dark:bg-red-500/5'
                : activeAction === 'override'
                  ? 'border-violet-300 dark:border-violet-500/40 bg-violet-50/60 dark:bg-violet-500/5'
                  : 'border-blue-300 dark:border-blue-500/40 bg-blue-50/60 dark:bg-blue-500/5'
            }`}
          >
            <Button
              variant={activeAction === 'cancel' ? 'danger' : 'primary'}
              size="sm"
              onClick={proceedToDetails}
              disabled={!hasSelection}
            >
              Continue
              {hasSelection && ` (${selectedIds.size} item${selectedIds.size > 1 ? 's' : ''})`}
            </Button>
            <Button variant="outline" size="sm" onClick={resetAction}>
              Discard
            </Button>
          </div>
        )}

        {/* Step 2 (shipment): Carrier + Tracking */}
        {activeAction === 'shipment' && actionStep === 'fill-details' && (
          <div className="border-t-2 border-blue-300 dark:border-blue-500/40 bg-blue-50/60 dark:bg-blue-500/5 px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400">
                  <PackageIcon />
                </span>
                <span className="text-xs font-semibold text-blue-700 dark:text-blue-400">
                  Create Shipment — Shipping Details
                </span>
              </div>
              <button
                onClick={resetAction}
                className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-text-primary transition-colors rounded-md px-2 py-1 hover:bg-surface-secondary"
              >
                <CloseIcon />
                Cancel
              </button>
            </div>

            <div className="rounded-lg border border-blue-200 dark:border-blue-500/20 bg-white dark:bg-surface p-3 space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                Items in shipment
              </p>
              {Object.entries(qtys)
                .filter(([id, q]) => selectedIds.has(id) && q > 0)
                .map(([id, q]) => {
                  const item = items.find((i) => i.id === id);
                  return (
                    <div key={id} className="flex items-center justify-between text-xs">
                      <span className="text-text-primary truncate">{item?.title ?? id}</span>
                      <span className="text-blue-600 dark:text-blue-400 font-medium shrink-0">×{q}</span>
                    </div>
                  );
                })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select
                label="Carrier"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                options={CARRIERS}
              />
              <Input
                label="Tracking Number"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter tracking number"
              />
            </div>
            <Input
              label="Custom Tracking URL (optional)"
              value={customTrackingUrl}
              onChange={(e) => setCustomTrackingUrl(e.target.value)}
              placeholder="Leave empty for auto-generated URL"
            />

            {carrier && trackingNumber && (
              <p className="text-[11px] text-text-tertiary">
                Tracking URL: {customTrackingUrl || buildTrackingUrl(carrier, trackingNumber) || 'N/A'}
              </p>
            )}

            {shipmentMutation.isError && (
              <p className="text-xs text-red-500">
                {shipmentMutation.error instanceof ApiClientError && shipmentMutation.error.apiError?.details?.length
                  ? shipmentMutation.error.apiError.details.map((d) => d.message).join('. ')
                  : (shipmentMutation.error as Error)?.message || 'Failed to create shipment'}
              </p>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="primary"
                size="sm"
                onClick={() => shipmentMutation.mutate()}
                disabled={!canSubmitShipment}
                loading={shipmentMutation.isPending}
              >
                Create Shipment
              </Button>
              <Button variant="outline" size="sm" onClick={() => setActionStep('select-items')}>
                Back
              </Button>
              <Button variant="outline" size="sm" onClick={resetAction}>
                Discard
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 (override): Assign Provider + Warehouse per item */}
        {activeAction === 'override' && actionStep === 'fill-details' && (
          <div className="border-t-2 border-violet-300 dark:border-violet-500/40 bg-violet-50/60 dark:bg-violet-500/5 px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400">
                  <OverrideIcon />
                </span>
                <span className="text-xs font-semibold text-violet-700 dark:text-violet-400">
                  Override Provider — Assign per Item
                </span>
              </div>
              <button
                onClick={resetAction}
                className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-text-primary transition-colors rounded-md px-2 py-1 hover:bg-surface-secondary"
              >
                <CloseIcon />
                Cancel
              </button>
            </div>

            <div className="max-h-[50vh] overflow-y-auto space-y-3">
              {[...selectedIds].map((id) => {
                const item = items.find((i) => i.id === id);
                if (!item) return null;
                return (
                  <OverrideItemRow
                    key={id}
                    item={item}
                    providerId={perItemProvider[id] ?? ''}
                    warehouseId={perItemWarehouse[id] ?? ''}
                    onProviderChange={(pid) => {
                      setPerItemProvider((p) => ({ ...p, [id]: pid }));
                      setPerItemWarehouse((w) => {
                        const next = { ...w };
                        delete next[id];
                        return next;
                      });
                    }}
                    onWarehouseChange={(wid) =>
                      setPerItemWarehouse((w) => ({ ...w, [id]: wid }))
                    }
                    api={api}
                  />
                );
              })}
            </div>

            <Input
              label="Reason (optional)"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Why is this override needed?"
            />

            {overrideMutation.isError && (
              <p className="text-xs text-red-500">
                {overrideMutation.error instanceof ApiClientError && overrideMutation.error.apiError?.details?.length
                  ? overrideMutation.error.apiError.details.map((d) => d.message).join('. ')
                  : (overrideMutation.error as Error)?.message || 'Failed to assign providers'}
              </p>
            )}

            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="primary"
                size="sm"
                onClick={() => overrideMutation.mutate()}
                disabled={!canSubmitOverride}
                loading={overrideMutation.isPending}
              >
                Assign Providers
              </Button>
              <Button variant="outline" size="sm" onClick={() => setActionStep('select-items')}>
                Back
              </Button>
              <Button variant="outline" size="sm" onClick={resetAction}>
                Discard
              </Button>
            </div>
          </div>
        )}

        {/* Existing shipments for this provider */}
        {relevantShipments.length > 0 && (
          <div className="border-t border-border px-5 py-3 space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
              Shipments
            </p>
            {relevantShipments.map((shipment) => (
              <div
                key={shipment.id}
                className="rounded-md border border-border p-2.5 space-y-1.5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        shipment.status === 'DELIVERED' ? 'success'
                          : shipment.status === 'CANCELLED' ? 'danger'
                            : shipment.status === 'IN_TRANSIT' ? 'info'
                              : 'default'
                      }
                      size="sm"
                    >
                      {shipment.status.replace(/_/g, ' ')}
                    </Badge>
                    {shipment.carrier && (
                      <span className="text-xs font-medium text-text-primary">{shipment.carrier}</span>
                    )}
                  </div>
                  {shipment.shippedAt && (
                    <span className="text-[10px] text-text-tertiary">
                      {new Date(shipment.shippedAt).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric',
                      })}
                    </span>
                  )}
                </div>
                {shipment.trackingNumber && (
                  <div className="text-xs text-text-secondary">
                    Tracking:{' '}
                    {shipment.trackingUrl ? (
                      <a
                        href={shipment.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-primary hover:underline"
                      >
                        {shipment.trackingNumber}
                      </a>
                    ) : (
                      shipment.trackingNumber
                    )}
                  </div>
                )}
                <div className="text-[10px] text-text-tertiary space-y-0.5">
                  {shipment.items
                    .filter((si) => itemIds.has(si.orderItemId))
                    .map((si) => {
                      const item = items.find((i) => i.id === si.orderItemId);
                      return (
                        <div key={si.orderItemId}>
                          {item?.title ?? si.orderItemId} ×{si.quantity}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Tracking button / form */}
        {!isTerminal && !isUnassigned && !activeAction && hasUnfulfilledItems && (
          <div className="border-t border-border px-5 py-3">
            {!showAddTracking ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddTracking(true)}
                className="w-full"
              >
                <span className="inline-flex items-center gap-1.5">
                  <TrackingIcon />
                  Add Tracking
                </span>
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-text-primary">Add Tracking</p>
                  <button
                    onClick={() => {
                      setShowAddTracking(false);
                      setTrackCarrier('');
                      setTrackNumber('');
                      setTrackCustomUrl('');
                    }}
                    className="text-xs text-text-tertiary hover:text-text-primary transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-[11px] text-text-secondary">
                  This will create a shipment for all unfulfilled items of this provider.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Select
                    label="Carrier"
                    value={trackCarrier}
                    onChange={(e) => setTrackCarrier(e.target.value)}
                    options={CARRIERS}
                  />
                  <Input
                    label="Tracking Number"
                    value={trackNumber}
                    onChange={(e) => setTrackNumber(e.target.value)}
                    placeholder="Enter tracking number"
                  />
                </div>
                <Input
                  label="Custom Tracking URL (optional)"
                  value={trackCustomUrl}
                  onChange={(e) => setTrackCustomUrl(e.target.value)}
                  placeholder="Leave empty for auto-generated URL"
                />
                {trackCarrier && trackNumber && (
                  <p className="text-[11px] text-text-tertiary">
                    Tracking URL: {trackCustomUrl || buildTrackingUrl(trackCarrier, trackNumber) || 'N/A'}
                  </p>
                )}
                {addTrackingMutation.isError && (
                  <p className="text-xs text-red-500">
                    {addTrackingMutation.error instanceof ApiClientError && addTrackingMutation.error.apiError?.details?.length
                      ? addTrackingMutation.error.apiError.details.map((d) => d.message).join('. ')
                      : (addTrackingMutation.error as Error)?.message || 'Failed to add tracking'}
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => addTrackingMutation.mutate()}
                    disabled={!canSubmitTracking}
                    loading={addTrackingMutation.isPending}
                  >
                    Add Tracking & Ship
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Terminal state footer */}
        {isTerminal && (
          <div className="border-t border-border px-5 py-3">
            <p className="text-xs text-text-tertiary">
              Final state — no further actions available.
            </p>
          </div>
        )}
      </Card>

      {/* Cancel confirmation modal */}
      <Modal
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        title="Confirm Cancellation"
        description="This action cannot be undone. The selected items will be cancelled."
        size="sm"
      >
        <div className="space-y-2 px-5 pb-2">
          {Object.entries(qtys)
            .filter(([id, q]) => selectedIds.has(id) && q > 0)
            .map(([itemId, qty]) => {
              const item = items.find((i) => i.id === itemId);
              return (
                <div key={itemId} className="flex items-center justify-between text-xs">
                  <span className="text-text-primary truncate">{item?.title ?? itemId}</span>
                  <span className="text-red-600 font-medium shrink-0">×{qty}</span>
                </div>
              );
            })}
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowCancelConfirm(false)} disabled={cancelMutation.isPending}>
            Go Back
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setShowCancelConfirm(false);
              cancelMutation.mutate();
            }}
            loading={cancelMutation.isPending}
          >
            Cancel Items
          </Button>
        </ModalFooter>
      </Modal>
      {dialog}
    </>
  );
}

// Icons

function EllipsisIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  );
}

function ProviderIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
      <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
      <path d="M12 3v6" />
    </svg>
  );
}

function PackageIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m7.5 4.27 9 5.15" />
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  );
}

function CancelIcon({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  );
}

function TrackingIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="16" height="13" x="1" y="3" rx="2" />
      <path d="m17 8 4 2v7a1 1 0 0 1-1 1h-2" />
      <circle cx="7.5" cy="18.5" r="1.5" />
      <circle cx="17.5" cy="18.5" r="1.5" />
    </svg>
  );
}

function OverrideIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 3h5v5" />
      <path d="M8 3H3v5" />
      <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3" />
      <path d="m15 9 6-6" />
    </svg>
  );
}

// Override per-item row with provider + warehouse selection
interface OverrideItemRowProps {
  item: OrderItem;
  providerId: string;
  warehouseId: string;
  onProviderChange: (id: string) => void;
  onWarehouseChange: (id: string) => void;
  api: ReturnType<typeof useApiClient>;
}

function OverrideItemRow({
  item,
  providerId,
  warehouseId,
  onProviderChange,
  onWarehouseChange,
  api,
}: OverrideItemRowProps) {
  const uptId = item.uptId ?? '';

  const providersQuery = useQuery({
    queryKey: ['admin', 'providers', 'active-for-upt', uptId],
    queryFn: () =>
      api.get<{ data: ProviderOption[] }>(
        uptId
          ? `/api/v1/admin/providers?activeOnly=true&uptIds=${uptId}`
          : '/api/v1/admin/providers?activeOnly=true',
      ),
  });

  const providers = providersQuery.data?.data ?? [];

  const providerDetailQuery = useQuery({
    queryKey: ['admin', 'providers', providerId],
    queryFn: () =>
      api.get<{ data: ProviderDetailResponse }>(
        `/api/v1/admin/providers/${providerId}`,
      ),
    enabled: !!providerId,
  });

  const warehouses =
    providerDetailQuery.data?.data?.warehouses?.filter((w) => w.isActive) ?? [];

  return (
    <div className="rounded-lg border border-violet-200 dark:border-violet-500/20 bg-white dark:bg-surface p-3 space-y-2">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-secondary text-sm">
          {item.uptType === 'APPAREL' ? '👕' : '🖨️'}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary truncate">
            {orderItemDisplayName(item)}
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[11px] text-text-secondary">
            {item.variantTitle && <span>{item.variantTitle}</span>}
            <span>&times; {item.quantity}</span>
            {item.providerName && (
              <span className="text-text-tertiary">current: {item.providerName}</span>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <label className="mb-0.5 block text-[11px] font-medium text-text-secondary">
            Provider
          </label>
          <Select
            value={providerId}
            onChange={(e) => onProviderChange(e.target.value)}
            options={[
              { value: '', label: 'Select provider...' },
              ...providers.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
          {providers.length === 0 && !providersQuery.isLoading && uptId && (
            <p className="mt-0.5 text-[10px] text-red-400">
              No providers mapped for this UPT.
            </p>
          )}
        </div>
        <div>
          <label className="mb-0.5 block text-[11px] font-medium text-text-secondary">
            Warehouse
          </label>
          <Select
            value={warehouseId}
            onChange={(e) => onWarehouseChange(e.target.value)}
            options={[
              { value: '', label: 'Select warehouse...' },
              ...warehouses.map((w) => ({
                value: w.id,
                label: `${w.name} (${w.code})`,
              })),
            ]}
            disabled={!providerId}
          />
        </div>
      </div>
    </div>
  );
}
